import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  MessageSquare,
  CreditCard,
  Settings,
  LogOut,
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
  Zap,
  BarChart3,
  SquareStack,
  Receipt,
  Shield,
  Briefcase,
  X,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useState } from "react";

const navGroups = [
  {
    label: "Main",
    items: [{ path: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "User Management",
    items: [
      { path: "/segments", label: "Segments", icon: SquareStack },
      { path: "/clients", label: "Clients", icon: Users },
    ],
  },
  {
    label: "Recovery",
    items: [
      { path: "/plans", label: "Payment Plans", icon: FileText },
      { path: "/debt-recoveries", label: "Debt Recoveries", icon: BarChart3 },
    ],
  },
  {
    label: "Engagement",
    items: [
      { path: "/campaigns", label: "Campaigns", icon: MessageSquare },
      { path: "/automations", label: "Automations", icon: Zap },
      { path: "/templates", label: "Templates", icon: FileText },
    ],
  },
  {
    label: "Finance",
    items: [
      { path: "/payments", label: "Payments", icon: CreditCard },
      { path: "/billing", label: "Billing", icon: Receipt },
    ],
  },
];

const bottomItems = [{ path: "/settings", label: "Settings", icon: Settings }];

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { logout, activeOrganization, user } = useAuth();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isSuperAdmin = user?.isSuperAdmin;

  const isActivePath = (path) => {
    if (location.pathname === path) return true;
    if (path !== "/dashboard" && location.pathname.startsWith(`${path}/`))
      return true;
    return false;
  };

  const renderNavBody = (collapsed, isMobile = false) => (
    <>
      {/* Logo Header */}
      <div
        className={`flex h-14 items-center justify-between ${
          collapsed ? "justify-center px-2" : "gap-2.5 px-3.5"
        } border-b border-slate-100 shrink-0`}
      >
        <div className="flex items-center gap-2.5 overflow-hidden min-w-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 shadow-xs shrink-0">
            <ShieldCheck className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden min-w-0">
              <h2 className="text-xs font-black text-slate-900 tracking-tight leading-none">
                Korvry
              </h2>
              <p className="text-[10px] text-slate-400 truncate max-w-[140px] font-medium mt-0.5 leading-none">
                {activeOrganization?.name || "Organization"}
              </p>
            </div>
          )}
        </div>
        {isMobile && (
          <button
            onClick={() => setMobileOpen && setMobileOpen(false)}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-3 scrollbar-none">
        {isSuperAdmin && (
          <NavLink
            to="/superadmin"
            onClick={() => isMobile && setMobileOpen && setMobileOpen(false)}
            className={`flex items-center ${
              collapsed ? "justify-center px-0" : "gap-2 px-2.5"
            } text-amber-600 group rounded-lg py-1.5 text-xs font-semibold hover:bg-amber-50 transition-all mb-1`}
            title={collapsed ? "Super Admin" : undefined}
          >
            <Shield className="h-3.5 w-3.5 shrink-0" />
            {!collapsed && <span>Super Admin</span>}
          </NavLink>
        )}

        {navGroups.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="px-2 mb-1 text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActivePath(item.path);

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => isMobile && setMobileOpen && setMobileOpen(false)}
                    className={`group flex items-center ${
                      collapsed ? "justify-center px-0" : "gap-2.5 px-2.5"
                    } rounded-lg py-1.5 text-xs font-semibold transition-all ${
                      active
                        ? "bg-indigo-50 text-indigo-700 font-bold shadow-xs"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon
                      className={`h-3.5 w-3.5 shrink-0 transition-colors ${
                        active
                          ? "text-indigo-600"
                          : "text-slate-400 group-hover:text-slate-600"
                      }`}
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate leading-none">{item.label}</span>
                        {active && (
                          <ChevronRight className="h-3 w-3 text-indigo-500 shrink-0" />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-slate-100 p-2 space-y-0.5 shrink-0">
        {user?.isBrm && (
          <NavLink
            to="/brm"
            onClick={() => isMobile && setMobileOpen && setMobileOpen(false)}
            className={`group flex items-center ${
              collapsed ? "justify-center px-0" : "gap-2.5 px-2.5"
            } rounded-lg py-1.5 text-xs font-semibold transition-all ${
              isActivePath("/brm")
                ? "bg-amber-50 text-amber-700 font-bold"
                : "text-amber-700 hover:bg-amber-50/80"
            }`}
            title={collapsed ? "Relationship Manager Portal" : undefined}
          >
            <Briefcase
              className={`h-3.5 w-3.5 shrink-0 ${
                isActivePath("/brm")
                  ? "text-amber-600"
                  : "text-amber-500 group-hover:text-amber-700"
              }`}
            />
            {!collapsed && (
              <span className="truncate leading-none">BRM Portal</span>
            )}
          </NavLink>
        )}

        {bottomItems.map((item) => {
          const Icon = item.icon;
          const active = isActivePath(item.path);

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => isMobile && setMobileOpen && setMobileOpen(false)}
              className={`group flex items-center ${
                collapsed ? "justify-center px-0" : "gap-2.5 px-2.5"
              } rounded-lg py-1.5 text-xs font-semibold transition-all ${
                active
                  ? "bg-indigo-50 text-indigo-700 font-bold"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon
                className={`h-3.5 w-3.5 shrink-0 ${
                  active
                    ? "text-indigo-600"
                    : "text-slate-400 group-hover:text-slate-600"
                }`}
              />
              {!collapsed && <span className="truncate leading-none">{item.label}</span>}
            </NavLink>
          );
        })}

        <button
          onClick={() => {
            if (isMobile && setMobileOpen) setMobileOpen(false);
            logout();
          }}
          className={`group flex w-full items-center ${
            collapsed ? "justify-center px-0" : "gap-2.5 px-2.5"
          } rounded-lg py-1.5 text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-600 transition-all`}
          title={collapsed ? "Sign Out" : undefined}
        >
          <LogOut className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-rose-500 transition-colors" />
          {!collapsed && <span className="leading-none">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* ── MOBILE OVERLAY & DRAWER ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileOpen && setMobileOpen(false)}
          />
          {/* Mobile Drawer */}
          <aside className="relative flex w-64 max-w-[80vw] flex-col bg-white shadow-2xl z-50 h-full">
            {renderNavBody(false, true)}
          </aside>
        </div>
      )}

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden md:flex relative flex-col border-r border-slate-200 bg-white transition-all duration-200 shrink-0 z-30 ${
          isCollapsed ? "w-16" : "w-56"
        }`}
      >
        {/* Floating Overflow Toggle Button */}
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-4 h-6 w-6 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-300 shadow-xs flex items-center justify-center transition-all z-40 focus:outline-none"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronLeft className="h-3 w-3" />
          )}
        </button>

        {renderNavBody(isCollapsed, false)}
      </aside>
    </>
  );
}
